import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/axiosPetition';
import { alertaGeneral } from '../../utils/alertas';
import { FaGlobe } from 'react-icons/fa';

const placeholderLogo = 'https://via.placeholder.com/120x80?text=Aliado';

const AliadosAdmin = () => {
  const [aliados, setAliados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editAliado, setEditAliado] = useState(null);
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    promociones: [''],
    contacto: '',
    telefono: '',
    correo: '',
    web: '',
    logo: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailAliado, setDetailAliado] = useState(null);

  // Cargar aliados al montar
  useEffect(() => {
    fetchAliados();
  }, []);

  const fetchAliados = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/aliados');
      // Normalizar el campo id
      const aliadosArray = Array.isArray(res.data.data)
        ? res.data.data.map(a => ({ ...a, id: a._id || a.id }))
        : [];
      setAliados(aliadosArray);
    } catch (err) {
      alertaGeneral({
        titulo: 'Error',
        messageHtml: 'Error al cargar aliados.',
        isError: true
      });
    } finally {
      setLoading(false);
    }
  };

  const openModal = (aliado = null) => {
    setEditAliado(aliado);
    setForm(aliado ? {
      ...aliado,
      promociones: aliado.promociones || ['']
    } : {
      nombre: '', descripcion: '', promociones: [''], contacto: '', telefono: '', correo: '', web: '', logo: ''
    });
    setLogoFile(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditAliado(null);
    setForm({ nombre: '', descripcion: '', promociones: [''], contacto: '', telefono: '', correo: '', web: '', logo: '' });
    setLogoFile(null);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'logo' && files && files[0]) {
      const file = files[0];
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
        alertaGeneral({
          titulo: 'Error',
          messageHtml: 'El logo debe ser una imagen (jpg, png, webp, gif).',
          isError: true
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        alertaGeneral({
          titulo: 'Error',
          messageHtml: 'El logo no debe superar los 2MB.',
          isError: true
        });
        return;
      }
      setLogoFile(file);
      setForm((prev) => ({ ...prev, logo: URL.createObjectURL(file) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePromocionChange = (idx, value) => {
    setForm((prev) => {
      const nuevas = [...prev.promociones];
      nuevas[idx] = value;
      return { ...prev, promociones: nuevas };
    });
  };

  const addPromocion = () => {
    setForm((prev) => ({ ...prev, promociones: [...prev.promociones, ''] }));
  };

  const removePromocion = (idx) => {
    setForm((prev) => {
      const nuevas = prev.promociones.filter((_, i) => i !== idx);
      return { ...prev, promociones: nuevas.length ? nuevas : [''] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.descripcion || !form.contacto || !form.telefono || !form.correo) {
      alertaGeneral({
        titulo: 'Campos requeridos',
        messageHtml: 'Completa todos los campos requeridos.',
        isError: true
      });
      return;
    }
    const formData = new FormData();
    formData.append('nombre', form.nombre);
    formData.append('descripcion', form.descripcion);
    formData.append('contacto', form.contacto);
    formData.append('telefono', form.telefono);
    formData.append('correo', form.correo);
    if (form.web) formData.append('web', form.web);
    if (logoFile) formData.append('logo', logoFile);
    if (form.promociones && form.promociones.length > 0) {
      formData.append('promociones', JSON.stringify(form.promociones.filter(p => p.trim() !== '')));
    }
    try {
      if (editAliado) {
        await apiClient.put(`/api/aliados/${editAliado.id}`, formData);
        alertaGeneral({
          titulo: 'Éxito',
          messageHtml: 'Aliado actualizado correctamente.',
          isError: false
        });
      } else {
        await apiClient.post('/api/aliados', formData);
        alertaGeneral({
          titulo: 'Éxito',
          messageHtml: 'Aliado creado correctamente.',
          isError: false
        });
      }
      closeModal();
      fetchAliados();
    } catch (err) {
      alertaGeneral({
        titulo: 'Error',
        messageHtml: err.response?.data?.message || 'Error al guardar el aliado.',
        isError: true
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este aliado?')) return;
    try {
      await apiClient.delete(`/api/aliados/${id}`);
      alertaGeneral({
        titulo: 'Éxito',
        messageHtml: 'Aliado eliminado correctamente.',
        isError: false
      });
      fetchAliados();
    } catch (err) {
      alertaGeneral({
        titulo: 'Error',
        messageHtml: 'Error al eliminar el aliado.',
        isError: true
      });
    }
  };

  const handleToggleEstado = async (aliado) => {
    const nuevoEstado = aliado.estado === 'activo' ? 'inactivo' : 'activo';
    try {
      await apiClient.patch(`/api/aliados/${aliado.id}/estado`, { estado: nuevoEstado });
      alertaGeneral({
        titulo: 'Éxito',
        messageHtml: `Aliado ${nuevoEstado === 'activo' ? 'activado' : 'inactivado'} correctamente.`,
        isError: false
      });
      fetchAliados();
    } catch (err) {
      alertaGeneral({
        titulo: 'Error',
        messageHtml: 'No se pudo cambiar el estado del aliado.',
        isError: true
      });
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#1f94c2ff]">Gestión de Aliados</h2>
        <button
          className="bg-[#f79865ff] text-white font-semibold px-4 py-2 rounded hover:bg-[#205087ff] transition"
          onClick={() => openModal()}
        >
          + Agregar Aliado
        </button>
      </div>
      {loading ? (
        <div className="text-center text-[#205087ff] py-10">Cargando aliados...</div>
      ) : aliados.length === 0 ? (
        <div className="text-center text-gray-500 py-10">No hay aliados disponibles.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {aliados.map((aliado) => (
            <div key={aliado.id} className="bg-white border border-gray-300 rounded-lg shadow p-8 flex flex-col items-center transition hover:shadow-lg relative">
              {/* Badge de estado */}
              <span className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${aliado.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>{aliado.estado === 'activo' ? 'Activo' : 'Inactivo'}</span>
              {/* Logo grande y centrado */}
              <img src={aliado.logo || placeholderLogo} alt={aliado.nombre} className="w-48 h-28 object-contain mb-4 rounded" />
              {/* Imagen secundaria si existe */}
              {aliado.imagenSecundaria && (
                <img src={aliado.imagenSecundaria} alt="Imagen secundaria" className="w-56 h-32 object-contain mb-4 rounded" />
              )}
              {/* Descripción */}
              <p className="text-gray-700 text-base mb-6 text-center">{aliado.descripcion}</p>
              {/* Botón Visitar (web) */}
              {aliado.web && (
                <a
                  href={aliado.web}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#1f94c2ff] hover:bg-[#205087ff] text-white font-semibold px-6 py-2 rounded shadow transition mt-4"
                >
                  <FaGlobe className="text-lg" />
                  {aliado.web.length > 30 ? 'Visitar' : aliado.web.replace(/^https?:\/\//, '')}
                </a>
              )}
              {/* Botones de acción */}
              <div className="flex gap-2 mt-4">
                <button
                  className="bg-[#1f94c2ff] text-white px-3 py-1 rounded hover:bg-[#205087ff] text-xs"
                  onClick={() => openModal(aliado)}
                >
                  Editar
                </button>
                <button
                  className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-700 text-xs"
                  onClick={() => handleDelete(aliado.id)}
                >
                  Eliminar
                </button>
                <button
                  className="bg-[#f79865ff] text-white px-3 py-1 rounded hover:bg-[#205087ff] text-xs"
                  onClick={() => { setDetailAliado(aliado); setShowDetail(true); }}
                >
                  Más detalles
                </button>
                <button
                  className={`px-3 py-1 rounded text-xs font-semibold ${aliado.estado === 'activo' ? 'bg-gray-400 hover:bg-gray-600 text-white' : 'bg-green-500 hover:bg-green-700 text-white'}`}
                  onClick={() => handleToggleEstado(aliado)}
                >
                  {aliado.estado === 'activo' ? 'Inactivar' : 'Activar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal crear / editar */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-3 sm:p-4 overflow-y-auto">
          <div className="relative w-full max-w-2xl lg:max-w-4xl max-h-[calc(100vh-1.5rem)] sm:max-h-[90vh] my-auto bg-white rounded-xl shadow-xl flex flex-col">
            <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 shrink-0">
              <h3 className="text-lg sm:text-xl font-bold text-[#1f94c2ff]">
                {editAliado ? 'Editar Aliado' : 'Agregar Aliado'}
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none p-1"
                onClick={closeModal}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1 overflow-y-auto px-5 sm:px-6 py-4 sm:py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
                <label className="font-semibold text-[#205087ff] text-sm md:col-span-2">
                  Nombre de la entidad
                  <input
                    type="text"
                    name="nombre"
                    value={form.nombre}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff]/40 focus:border-[#1f94c2ff]"
                    required
                  />
                </label>

                <label className="font-semibold text-[#205087ff] text-sm md:col-span-2">
                  Descripción de la alianza
                  <textarea
                    name="descripcion"
                    value={form.descripcion}
                    onChange={handleChange}
                    rows={4}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base resize-y min-h-[5rem] focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff]/40 focus:border-[#1f94c2ff]"
                    required
                  />
                </label>

                <label className="font-semibold text-[#205087ff] text-sm">
                  Nombre del contacto
                  <input
                    type="text"
                    name="contacto"
                    value={form.contacto}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff]/40 focus:border-[#1f94c2ff]"
                    required
                  />
                </label>

                <label className="font-semibold text-[#205087ff] text-sm">
                  Teléfono
                  <input
                    type="text"
                    name="telefono"
                    value={form.telefono}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff]/40 focus:border-[#1f94c2ff]"
                    required
                  />
                </label>

                <label className="font-semibold text-[#205087ff] text-sm">
                  Correo
                  <input
                    type="email"
                    name="correo"
                    value={form.correo}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff]/40 focus:border-[#1f94c2ff]"
                    required
                  />
                </label>

                <label className="font-semibold text-[#205087ff] text-sm">
                  Web (opcional)
                  <input
                    type="text"
                    name="web"
                    value={form.web}
                    onChange={handleChange}
                    placeholder="https://"
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff]/40 focus:border-[#1f94c2ff]"
                  />
                </label>

                <div className="font-semibold text-[#205087ff] text-sm md:col-span-2">
                  Logo / Imagen (opcional)
                  <div className="mt-2 flex flex-col sm:flex-row sm:items-start gap-4">
                    <input
                      type="file"
                      name="logo"
                      accept="image/*"
                      onChange={handleChange}
                      className="block w-full sm:flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#1f94c2ff] file:text-white file:font-medium hover:file:bg-[#205087ff] file:cursor-pointer"
                    />
                    {form.logo && (
                      <div className="shrink-0 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-2 w-full sm:w-40 h-24">
                        <img src={form.logo} alt="Vista previa del logo" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 mt-5 pt-4 border-t border-gray-100 shrink-0">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto bg-[#1f94c2ff] text-white font-semibold px-5 py-2 rounded-lg hover:bg-[#205087ff] transition"
                >
                  {editAliado ? 'Guardar cambios' : 'Agregar aliado'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de detalles */}
      {showDetail && detailAliado && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl"
              onClick={() => setShowDetail(false)}
            >
              ×
            </button>
            <h3 className="text-xl font-bold mb-4 text-[#1f94c2ff]">Detalles del Aliado</h3>
            <img src={detailAliado.logo || placeholderLogo} alt={detailAliado.nombre} className="w-48 h-28 object-contain mb-4 rounded mx-auto" />
            <div className="mb-2"><b>Nombre:</b> {detailAliado.nombre}</div>
            <div className="mb-2"><b>Descripción:</b> {detailAliado.descripcion}</div>
            <div className="mb-2"><b>Contacto:</b> {detailAliado.contacto}</div>
            <div className="mb-2"><b>Teléfono:</b> {detailAliado.telefono}</div>
            <div className="mb-2"><b>Correo:</b> {detailAliado.correo}</div>
            {detailAliado.web && (
              <div className="mb-2"><b>Web:</b> <a href={detailAliado.web} target="_blank" rel="noopener noreferrer" className="text-[#1f94c2ff] underline">{detailAliado.web}</a></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AliadosAdmin; 